import { useState, useEffect } from 'react'
import { seoApi, api, type ApiSeoReport, type ApiSeoIssue, redirectsApi, type ApiRedirect } from '../utils/api'
import { useEditorStore } from '../store/editor.store'

interface Props {
  siteId: string | null
  onClose: () => void
}

type PanelTab = 'audit' | 'redirects' | 'vitals' | 'ab-tests'

const ISSUE_COLORS = { error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
const ISSUE_LABELS = { error: 'Ошибка', warning: 'Предупреждение', info: 'Совет' }

function ScoreCircle({ score }: { score: number }) {
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444'
  const r = 44
  const circ = 2 * Math.PI * r
  const dash = circ * (score / 100)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="var(--bg-3)" strokeWidth="10" />
        <circle
          cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <text x="55" y="55" dominantBaseline="middle" textAnchor="middle"
          style={{ fontSize: '24px', fontWeight: 800, fill: color, fontFamily: 'inherit' }}>
          {score}
        </text>
        <text x="55" y="72" dominantBaseline="middle" textAnchor="middle"
          style={{ fontSize: '10px', fill: 'var(--text-muted)', fontFamily: 'inherit' }}>
          / 100
        </text>
      </svg>
      <div style={{ fontSize: '13px', fontWeight: 700, color }}>
        {score >= 80 ? 'Хорошо' : score >= 50 ? 'Нужна работа' : 'Много проблем'}
      </div>
    </div>
  )
}

function IssueRow({ issue }: { issue: ApiSeoIssue }) {
  const color = ISSUE_COLORS[issue.type]
  const label = ISSUE_LABELS[issue.type]
  return (
    <div style={{ display: 'flex', gap: '10px', padding: '10px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0, marginTop: '1px' }}>
        <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, background: `${color}22`, color }}>{label}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 }}>{issue.message}</div>
        {issue.page && (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', fontFamily: 'monospace' }}>{issue.page}</div>
        )}
      </div>
    </div>
  )
}

function RedirectsTab({ siteId }: { siteId: string }) {
  const [items, setItems] = useState<ApiRedirect[]>([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ from: '', to: '', permanent: true })
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    redirectsApi.list(siteId).then(setItems).catch(() => setItems([])).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [siteId])

  const handleCreate = async () => {
    if (!form.from.trim() || !form.to.trim()) return
    setSaving(true)
    try {
      await redirectsApi.create(siteId, {
        from: form.from.startsWith('/') ? form.from : '/' + form.from,
        to: form.to,
        permanent: form.permanent,
      })
      setForm({ from: '', to: '', permanent: true })
      setCreating(false)
      load()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  const toggleActive = async (item: ApiRedirect) => {
    await redirectsApi.update(siteId, item.id, { active: !item.active }).catch(() => null)
    load()
  }

  const handleDelete = async (item: ApiRedirect) => {
    if (!confirm(`Удалить редирект ${item.from} → ${item.to}?`)) return
    await redirectsApi.delete(siteId, item.id).catch(() => null)
    load()
  }

  return (
    <div style={{ padding: '20px 24px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Настройте редиректы для старых URL</div>
        <button
          onClick={() => setCreating(v => !v)}
          style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
          + Добавить
        </button>
      </div>

      {creating && (
        <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px' }}>Новый редирект</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center' }}>
            <input
              className="prop-input"
              placeholder="Откуда: /old-page"
              value={form.from}
              onChange={e => setForm(f => ({ ...f, from: e.target.value }))}
            />
            <span style={{ fontSize: '16px', color: 'var(--text-muted)', textAlign: 'center' }}>→</span>
            <input
              className="prop-input"
              placeholder="Куда: /new-page или https://..."
              value={form.to}
              onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.permanent} onChange={e => setForm(f => ({ ...f, permanent: e.target.checked }))} />
            301 Постоянный редирект (рекомендуется для SEO)
          </label>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setCreating(false)} style={{ padding: '6px 14px', background: 'var(--bg-3)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>Отмена</button>
            <button
              onClick={handleCreate}
              disabled={saving || !form.from.trim() || !form.to.trim()}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.from.trim() || !form.to.trim() ? 0.6 : 1 }}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>}

      {!loading && items.length === 0 && !creating && (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>↪</div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Нет редиректов</div>
          <div style={{ fontSize: '13px', lineHeight: 1.5 }}>Добавьте редиректы чтобы сохранить SEO при изменении URL страниц</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map(item => (
          <div key={item.id} style={{ background: 'var(--bg-2)', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', opacity: item.active ? 1 : 0.55 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <code style={{ fontSize: '12px', color: 'var(--text-primary)', background: 'var(--bg-3)', padding: '2px 6px', borderRadius: '4px' }}>{item.from}</code>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>→</span>
                <code style={{ fontSize: '12px', color: 'var(--accent)', background: 'var(--bg-3)', padding: '2px 6px', borderRadius: '4px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{item.to}</code>
                <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '4px', background: item.permanent ? '#ede9fe' : '#e0f2fe', color: item.permanent ? '#7c3aed' : '#0369a1' }}>
                  {item.permanent ? '301' : '302'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                onClick={() => toggleActive(item)}
                style={{ padding: '4px 10px', border: '1px solid var(--border)', background: item.active ? '#dcfce7' : 'var(--bg-3)', color: item.active ? '#15803d' : 'var(--text-muted)', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
              >
                {item.active ? 'Вкл' : 'Выкл'}
              </button>
              <button
                onClick={() => handleDelete(item)}
                style={{ padding: '4px 8px', border: '1px solid #fecaca', background: 'transparent', color: '#ef4444', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
              >✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface VitalMetric {
  id: string
  label: string
  value: string
  score: number
  good: string
  poor: string
  unit: string
}

function VitalBar({ metric }: { metric: VitalMetric }) {
  const color = metric.score >= 0.9 ? '#22c55e' : metric.score >= 0.5 ? '#f59e0b' : '#ef4444'
  const status = metric.score >= 0.9 ? 'Хорошо' : metric.score >= 0.5 ? 'Нужна работа' : 'Плохо'
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{metric.label}</span>
          <span style={{ fontSize: '13px', fontWeight: 700, color }}>{metric.value}{metric.unit}</span>
        </div>
        <div style={{ height: '4px', background: 'var(--bg-3)', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.round(metric.score * 100)}%`, background: color, borderRadius: '2px', transition: 'width 0.6s ease' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Хорошо: {metric.good}</span>
          <span style={{ fontSize: '10px', fontWeight: 600, color }}>{status}</span>
        </div>
      </div>
    </div>
  )
}

interface AbTest {
  id: string; name: string; pageSlug: string; blockId: string; status: string; createdAt: string
  stats: { A: { views: number; conversions: number; rate: number }; B: { views: number; conversions: number; rate: number } }
}

function AbTestsTab({ siteId }: { siteId: string | null }) {
  const [tests, setTests] = useState<AbTest[]>([])
  const [loading, setLoading] = useState(false)

  const load = () => {
    if (!siteId) return
    setLoading(true)
    api.get<AbTest[]>(`/sites/${siteId}/ab-tests`).then(setTests).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [siteId])

  const updateStatus = async (testId: string, status: string) => {
    if (!siteId) return
    await api.patch(`/sites/${siteId}/ab-tests/${testId}/status`, { status }).catch(() => null)
    load()
  }

  const deleteTest = async (testId: string) => {
    if (!siteId || !confirm('Удалить A/B тест?')) return
    await api.delete(`/sites/${siteId}/ab-tests/${testId}`).catch(() => null)
    load()
  }

  if (!siteId) return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>Сохраните сайт для просмотра A/B тестов</div>

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>A/B Тесты</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Создавайте тесты в свойствах блока, помечая блок как вариант A или B</div>
        </div>
        <button onClick={load} style={{ padding: '6px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
          {loading ? '...' : '↻ Обновить'}
        </button>
      </div>

      {tests.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--text-muted)', fontSize: '13px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🧪</div>
          <div style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>Нет активных тестов</div>
          <div style={{ lineHeight: 1.6 }}>В свойствах блока задайте A/B Test ID и вариант (A или B). Тест создастся автоматически при публикации.</div>
        </div>
      )}

      {tests.map(test => {
        const winnerIsA = test.stats.A.rate >= test.stats.B.rate
        return (
          <div key={test.id} style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '14px', background: 'var(--bg-1)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{test.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Страница: {test.pageSlug} • Блок: {test.blockId.slice(0,8)}…</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: test.status === 'RUNNING' ? '#dcfce7' : test.status === 'PAUSED' ? '#fef9c3' : '#f1f5f9', color: test.status === 'RUNNING' ? '#15803d' : test.status === 'PAUSED' ? '#854d0e' : '#64748b' }}>
                  {test.status === 'RUNNING' ? '▶ Запущен' : test.status === 'PAUSED' ? '⏸ Пауза' : '■ Завершён'}
                </span>
                <button onClick={() => deleteTest(test.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '2px 4px' }}>×</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              {(['A', 'B'] as const).map(variant => {
                const s = test.stats[variant]
                const isWinner = (variant === 'A') === winnerIsA && (s.views > 0)
                return (
                  <div key={variant} style={{ padding: '10px 12px', background: isWinner ? '#f0fdf4' : 'var(--bg-2)', borderRadius: '8px', border: isWinner ? '1px solid #86efac' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: isWinner ? '#15803d' : 'var(--text-muted)', marginBottom: '6px' }}>Вариант {variant}{isWinner ? ' 🏆' : ''}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>{s.views}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Просм.</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>{s.conversions}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Конв.</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: s.rate > 0 ? '#5b6af0' : 'var(--text-primary)' }}>{s.rate}%</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>CR</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: '6px' }}>
              {test.status !== 'RUNNING' && <button onClick={() => updateStatus(test.id, 'RUNNING')} style={{ flex: 1, padding: '6px', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>▶ Запустить</button>}
              {test.status === 'RUNNING' && <button onClick={() => updateStatus(test.id, 'PAUSED')} style={{ flex: 1, padding: '6px', background: '#fef9c3', color: '#854d0e', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>⏸ Пауза</button>}
              {test.status !== 'ENDED' && <button onClick={() => updateStatus(test.id, 'ENDED')} style={{ flex: 1, padding: '6px', background: 'var(--bg-2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>■ Завершить</button>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function CoreWebVitalsTab({ siteSlug, customDomain }: { siteSlug: string; customDomain?: string }) {
  const [metrics, setMetrics] = useState<VitalMetric[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>('mobile')
  const [perfScore, setPerfScore] = useState<number | null>(null)

  const siteUrl = customDomain ? `https://${customDomain}` : `https://${siteSlug}.mybst.ru`

  async function fetchVitals() {
    setLoading(true)
    setError('')
    setMetrics([])
    setPerfScore(null)
    try {
      const url = `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(siteUrl)}&strategy=${strategy}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Ошибка API')
      const data = await res.json() as {
        lighthouseResult?: {
          categories?: { performance?: { score?: number } }
          audits?: Record<string, { numericValue?: number; score?: number; displayValue?: string }>
        }
      }
      const audits = data.lighthouseResult?.audits ?? {}
      const perf = data.lighthouseResult?.categories?.performance?.score
      if (perf !== undefined) setPerfScore(Math.round(perf * 100))

      const extract = (key: string, label: string, good: string, poor: string, unit: string): VitalMetric => {
        const a = audits[key]
        const numVal = a?.numericValue ?? 0
        const score = a?.score ?? 0
        let value = a?.displayValue?.replace(/\s*s$/, '').replace(/\s*ms$/, '') ?? '—'
        if (unit === 's' && numVal > 0) value = (numVal / 1000).toFixed(1)
        if (unit === 'ms' && numVal > 0) value = Math.round(numVal).toString()
        if (unit === '') value = numVal > 0 ? numVal.toFixed(3) : '—'
        return { id: key, label, value, score, good, poor, unit }
      }

      setMetrics([
        extract('largest-contentful-paint', 'LCP (Largest Contentful Paint)', '< 2.5s', '> 4s', 's'),
        extract('first-contentful-paint', 'FCP (First Contentful Paint)', '< 1.8s', '> 3s', 's'),
        extract('total-blocking-time', 'TBT (Total Blocking Time)', '< 200ms', '> 600ms', 'ms'),
        extract('cumulative-layout-shift', 'CLS (Cumulative Layout Shift)', '< 0.1', '> 0.25', ''),
        extract('speed-index', 'Speed Index', '< 3.4s', '> 5.8s', 's'),
        extract('interactive', 'TTI (Time to Interactive)', '< 3.8s', '> 7.3s', 's'),
      ])
    } catch (e) {
      setError('Не удалось загрузить данные. Убедитесь, что сайт опубликован.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          Проверяет реальные показатели производительности через Google PageSpeed Insights.
          Сайт должен быть опубликован.
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
          <code style={{ fontSize: '11px', background: 'var(--bg-2)', padding: '4px 8px', borderRadius: '6px', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {siteUrl}
          </code>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: 'var(--bg-2)', borderRadius: '8px', padding: '2px', gap: '2px' }}>
            {(['mobile', 'desktop'] as const).map(s => (
              <button key={s} onClick={() => setStrategy(s)}
                style={{ padding: '5px 12px', background: strategy === s ? 'var(--accent)' : 'transparent', color: strategy === s ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                {s === 'mobile' ? '📱 Мобильный' : '🖥 Desktop'}
              </button>
            ))}
          </div>
          <button onClick={fetchVitals} disabled={loading}
            style={{ padding: '7px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Анализ...' : metrics.length ? 'Перепроверить' : 'Проверить'}
          </button>
        </div>
      </div>

      {error && <div style={{ padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>Загружаем данные PageSpeed Insights...</div>}

      {!loading && metrics.length > 0 && (
        <>
          {perfScore !== null && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
              <ScoreCircle score={perfScore} />
            </div>
          )}
          <div>
            {metrics.map(m => <VitalBar key={m.id} metric={m} />)}
          </div>
          <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-2)', borderRadius: '8px', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Данные предоставлены Google PageSpeed Insights. LCP, FID/TBT, CLS — метрики Core Web Vitals, влияющие на позиции в поиске.
          </div>
        </>
      )}

      {!loading && metrics.length === 0 && !error && (
        <div style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Core Web Vitals</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Нажмите «Проверить» для анализа LCP, FCP, TBT, CLS и Speed Index вашего сайта
          </div>
        </div>
      )}
    </div>
  )
}

export function SeoAuditPanel({ siteId, onClose }: Props) {
  const [tab, setTab] = useState<PanelTab>('audit')
  const [report, setReport] = useState<ApiSeoReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [error, setError] = useState('')
  const { page, updatePage } = useEditorStore()

  async function runAudit() {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const r = await seoApi.audit(siteId)
      setReport(r)
    } catch (e) { setError(String(e)) }
    finally { setLoading(false) }
  }

  async function improveWithAi() {
    setAiLoading(true)
    setError('')
    try {
      const content = page.sections
        .flatMap(s => s.blocks)
        .map(b => String(b.data?.text ?? b.data?.title ?? b.data?.subtitle ?? ''))
        .filter(Boolean)
        .join(' ')
        .slice(0, 800)
      const result = await api.post<{ seoTitle: string; seoDesc: string; keywords: string[] }>('/ai/improve-seo', {
        title: page.title,
        content,
      })
      updatePage({ seoTitle: result.seoTitle, seoDesc: result.seoDesc })
      setError('')
    } catch (e) { setError(String(e)) }
    finally { setAiLoading(false) }
  }

  const grouped = report ? {
    error: report.issues.filter(i => i.type === 'error'),
    warning: report.issues.filter(i => i.type === 'warning'),
    info: report.issues.filter(i => i.type === 'info'),
  } : null

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ width: '520px', maxWidth: '95vw', height: '100vh', background: 'var(--bg-1)', borderLeft: '1px solid var(--border)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-1)', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>SEO</div>
              {tab === 'audit' && report && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                {report.pageCount} стр. · {new Date(report.checkedAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
              </div>}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {tab === 'audit' && (
                <>
                  <button
                    onClick={improveWithAi}
                    disabled={aiLoading}
                    style={{ padding: '6px 12px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: aiLoading ? 0.7 : 1 }}
                    title="AI улучшит title и description текущей страницы"
                  >{aiLoading ? '...' : '✨ AI SEO'}</button>
                  {siteId && (
                    <a
                      href={`/api/sites/${siteId}/seo/sitemap`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ padding: '6px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none', cursor: 'pointer' }}
                    >XML Sitemap ↗</a>
                  )}
                  <button
                    onClick={runAudit}
                    disabled={loading || !siteId}
                    style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
                  >{loading ? 'Проверяем...' : report ? 'Перепроверить' : 'Запустить аудит'}</button>
                </>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            {([['audit', '🔍 Аудит'], ['redirects', '↪ Редиректы'], ['vitals', '⚡ Core Web Vitals'], ['ab-tests', '🧪 A/B Тесты']] as [PanelTab, string][]).map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: '13px', fontWeight: 600, color: tab === t ? 'var(--accent)' : 'var(--text-muted)', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', cursor: 'pointer', marginBottom: '-1px' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'redirects' ? (
          siteId ? <RedirectsTab siteId={siteId} /> : <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>Сохраните сайт для управления редиректами</div>
        ) : null}

        {tab === 'vitals' ? (
          <CoreWebVitalsTab
            siteSlug={localStorage.getItem('bst-site-slug') ?? 'my-site'}
            customDomain={useEditorStore.getState().settings.customDomain || undefined}
          />
        ) : null}

        {tab === 'ab-tests' ? (
          <AbTestsTab siteId={siteId} />
        ) : null}

        <div style={{ padding: '24px', flex: 1, display: tab === 'redirects' || tab === 'vitals' || tab === 'ab-tests' ? 'none' : 'block' }}>
          {error && (
            <div style={{ padding: '12px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>
          )}

          {!report && !loading && (
            <div style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Проверьте SEO вашего сайта</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Аудит проверит заголовки, мета-описания, H1, alt-тексты изображений, настройки индексации и другие факторы.
              </div>
              <button
                onClick={runAudit}
                disabled={!siteId}
                style={{ marginTop: '24px', padding: '10px 28px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
              >Запустить аудит</button>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--text-muted)' }}>Анализируем сайт...</div>
          )}

          {report && grouped && (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <ScoreCircle score={report.score} />
              </div>

              {/* Issue counts */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '24px' }}>
                {(['error', 'warning', 'info'] as const).map(type => (
                  <div key={type} style={{ background: 'var(--bg-2)', border: `1px solid ${ISSUE_COLORS[type]}44`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 800, color: ISSUE_COLORS[type] }}>{grouped[type].length}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{ISSUE_LABELS[type]}</div>
                  </div>
                ))}
              </div>

              {report.issues.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', background: '#dcfce7', borderRadius: '12px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#15803d' }}>Всё отлично!</div>
                  <div style={{ fontSize: '13px', color: '#166534', marginTop: '4px' }}>Проблем не обнаружено</div>
                </div>
              ) : (
                <>
                  {(['error', 'warning', 'info'] as const).map(type =>
                    grouped[type].length > 0 && (
                      <div key={type} style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: ISSUE_COLORS[type], textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>
                          {ISSUE_LABELS[type]}ы ({grouped[type].length})
                        </div>
                        {grouped[type].map((issue, i) => <IssueRow key={i} issue={issue} />)}
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
